import React, { FC, useCallback, useEffect, useState } from 'react'

import { IdcardTwoTone } from '@ant-design/icons'
import { Button, Col, Form, Input, PageHeader, Radio, Row } from 'antd'
import { MaskedInput } from 'antd-mask-input'
import ID_COMPONENTE from 'config/idComponente'
import cnpj from 'Hooks/useMask/cnpj'
import { Empresa } from 'Models/cadastros'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import WsCadastroService from 'Services/WS_CADASTRO'
import { MsgSuccessOptions } from 'Types/SnackbarTypes'

import IFormEmpresasInput from './IFormEmpresasInput'

interface IProps {
  mode: string
}

const FormEmpresas: FC<IProps> = ({ mode }: IProps) => {
  const navigate = useNavigate()
  const [openSnackbarSuccess, closeSnackbarSuccess] =
    useSnackbar(MsgSuccessOptions)
  const [itemData, setItemData] = useState<Empresa>()
  const [carregandoInclusaoOuEdicao, setCarregandoInclusaoOuEdicao] =
    useState<boolean>(false)
  const { id } = useParams()
  const [formulario] = Form.useForm<Empresa>()

  const getItemData = useCallback(async (idEmpresa) => {
    const { data } = await WsCadastroService.empresas.details(
      ID_COMPONENTE.DOCAS,
      idEmpresa,
    )

    data.cnpj = ajustaCNPJ(data.cnpj)

    setItemData(data)
  }, [])

  useEffect(() => {
    if (id) {
      getItemData(parseInt(id))
    }
  }, [id, getItemData])

  const cadastraOuEditaEmpresa = async (empresa: Empresa) => {
    setCarregandoInclusaoOuEdicao(true)

    empresa.cnpj = cnpj.clean(empresa.cnpj).toString()

    if (empresa.ativo === undefined) {
      empresa.ativo = true
    }

    const { data } =
      mode === 'add'
        ? await WsCadastroService.empresas.create(ID_COMPONENTE.DOCAS, empresa)
        : await WsCadastroService.empresas.update(
            ID_COMPONENTE.DOCAS,
            empresa,
            id,
          )

    if (data) {
      if (data.mensagem) {
        openSnackbarSuccess(data.mensagem)
      }

      if (data.statusCode === 200 || data.statusCode === 201) {
        navigate(-1)
      } else {
        setCarregandoInclusaoOuEdicao(false)
      }
    }
  }

  useEffect(() => {
    if (itemData) {
      formulario.setFieldsValue(itemData)
    }
  }, [itemData])

  const ajustaCNPJ = (cnpj: string) => {
    let cnpjAjustado: string = cnpj

    while (cnpjAjustado.length < 14) {
      cnpjAjustado = '0' + cnpjAjustado
    }

    return cnpjAjustado
  }

  return (
    <Form onFinish={cadastraOuEditaEmpresa} layout="vertical" form={formulario}>
      <PageHeader
        ghost={false}
        title={mode === 'add' ? 'Incluir Empresa' : 'Editar Empresa'}
        extra={[
          <Button
            key="insercaoOuEdicao"
            type="primary"
            htmlType="submit"
            loading={carregandoInclusaoOuEdicao}
          >
            {mode === 'add' ? 'Incluir' : 'Salvar'}
          </Button>,

          <Button key="voltar" type="primary" onClick={() => navigate(-1)}>
            Voltar
          </Button>,
        ]}
      >
        <Row gutter={16}>
          <Col span={6} md={6} sm={12} xs={24}>
            <Form.Item
              name="nome"
              label="Nome:"
              rules={[{ required: true, message: 'Campo obrigatório' }]}
            >
              <Input type="text" />
            </Form.Item>
          </Col>

          <Col span={6} md={6} sm={12} xs={24}>
            <Form.Item
              name="cnpj"
              label="CNPJ:"
              rules={[{ required: true, message: 'Campo obrigatório' }]}
            >
              <MaskedInput mask="11.111.111/1111-11" />
            </Form.Item>
          </Col>

          <Col span={4} md={4} sm={8} xs={24}>
            <Form.Item name="ativo" label="Situação:">
              <Radio.Group defaultValue={true}>
                <Radio value={true}>Ativo</Radio>
                <Radio value={false}>Inativo</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={2} md={2} sm={4} xs={6}>
            <Form.Item name="ddd" label="DDD:">
              <Input type="number" disabled={true} />
            </Form.Item>
          </Col>

          <Col span={6} md={6} sm={12} xs={18}>
            <Form.Item name="telefone" label="Telefone:">
              <Input type="number" disabled={true} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6} md={6} sm={12} xs={24}>
            <Form.Item name="pais" label="País:">
              <Input type="text" disabled={true} />
            </Form.Item>
          </Col>

          <Col span={6} md={6} sm={12} xs={24}>
            <Form.Item name="uf" label="UF:">
              <Input type="text" disabled={true} />
            </Form.Item>
          </Col>

          <Col span={6} md={6} sm={12} xs={24}>
            <Form.Item name="cidade" label="Cidade:">
              <Input type="text" disabled={true} />
            </Form.Item>
          </Col>

          <Col span={6} md={6} sm={12} xs={24}>
            <Form.Item name="bairro" label="Bairro:">
              <Input type="text" disabled={true} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6} md={6} sm={12} xs={24}>
            <Form.Item name="cep" label="CEP:">
              <Input type="number" disabled={true} />
            </Form.Item>
          </Col>

          <Col span={9} md={9} sm={18} xs={24}>
            <Form.Item name="endereco" label="Endereço:">
              <Input type="text" disabled={true} />
            </Form.Item>
          </Col>

          <Col span={3} md={3} sm={18} xs={24}>
            <Form.Item name="numero" label="Número:">
              <Input type="number" disabled={true} />
            </Form.Item>
          </Col>

          <Col span={6} md={6} sm={12} xs={24}>
            <Form.Item name="complemento" label="Complemento:">
              <Input type="text" disabled={true} />
            </Form.Item>
          </Col>
        </Row>
      </PageHeader>
    </Form>
  )
}

export default FormEmpresas
